/**
 * Screen ↔ photo through the backdrop framing — the host's copy of the
 * viewer's `src/engine/backgroundFraming.ts`.
 *
 * The canvas is a cross-origin iframe, so the host cannot ask the viewer where a
 * click landed in the photograph; it has to do the viewer's arithmetic itself.
 * `computeBackgroundUvMatrix` and `screenUvForTextureUv` below are copied
 * VERBATIM (tile support included, so the two can be compared numerically —
 * `scripts/check-onbody.ts` does, against the viewer's own file when the sibling
 * repo is on disk). Do not "simplify" them: the viewer inverts this exact matrix
 * in `placeOnBody`, and any drift puts the ring beside the finger.
 *
 * Conventions:
 *   - screen fractions `sx, sy` run 0..1 across the canvas, y DOWN (DOM);
 *   - screen uv runs 0..1 with the origin BOTTOM-left (the plane background's uv);
 *   - photo points are normalised to the photo, y DOWN (`OnBodyAnchor`);
 *   - `flipY`: whether texture v runs bottom-up over the photo — true for a
 *     backdrop loaded by URL (three flips an HTMLImageElement on upload), false
 *     for one picked from disk (`setBackgroundImageFile` uploads an ImageBitmap,
 *     which ignores UNPACK_FLIP_Y). The viewer's `_backdropMapping` makes the
 *     same call, which is what keeps a click and its placement exact inverses.
 */
import type { BackgroundImageFraming } from "../../../shared/api-contract";

/** The framing every pre-v49 scene rendered with. */
export const DEFAULT_BACKGROUND_FRAMING: Required<BackgroundImageFraming> = {
  fit: "cover",
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
};

export interface BackgroundTile {
  tx: number;
  ty: number;
  tilesX: number;
  tilesY: number;
}

/** A framing with every default filled in, as the viewer applies it. */
export function fullFraming(f: BackgroundImageFraming | null | undefined): Required<BackgroundImageFraming> {
  return { ...DEFAULT_BACKGROUND_FRAMING, ...(f ?? {}) } as Required<BackgroundImageFraming>;
}

// ─── Verbatim from webgl-jewelry-viewer/src/engine/backgroundFraming.ts ─────

/**
 * The uv transform for a 2D backdrop, as the nine row-major numbers of the
 * matrix three copies into its `uvTransform` uniform. Maps a tile's incoming uv
 * to source uv:
 *
 *   v_full  = (v + tileOffset) / tiles
 *   p       = v_full - 0.5
 *   p'      = A⁻¹ · R(θ) · A · p                // A = diag(viewAspect, 1)
 *   sampled = diag(rx, ry) · p' + cropCentre
 */
export function computeBackgroundUvMatrix(
  framing: Required<BackgroundImageFraming>,
  viewAspect: number,
  imageAspect: number,
  tile?: BackgroundTile,
): number[] {
  let rx = 1;
  let ry = 1;
  if (imageAspect > 0 && viewAspect > 0 && framing.fit !== "stretch") {
    const wide = imageAspect > viewAspect;
    if (framing.fit === "contain") {
      if (wide) ry = imageAspect / viewAspect;
      else rx = viewAspect / imageAspect;
    } else {
      if (wide) rx = viewAspect / imageAspect;
      else ry = imageAspect / viewAspect;
    }
  }

  const zoom = framing.scale > 0 ? framing.scale : 1;
  rx /= zoom;
  ry /= zoom;

  const ccx = 0.5 - framing.offsetX * rx;
  const ccy = 0.5 - framing.offsetY * ry;

  const tilesX = tile ? tile.tilesX : 1;
  const tilesY = tile ? tile.tilesY : 1;
  const otx = tile ? tile.tx : 0;
  const oty = tile ? tile.tilesY - 1 - tile.ty : 0;
  const ax = 1 / tilesX;
  const ay = 1 / tilesY;
  const bx = otx / tilesX - 0.5;
  const by = oty / tilesY - 0.5;

  const a = viewAspect > 0 ? viewAspect : 1;
  const c = Math.cos(framing.rotation);
  const s = Math.sin(framing.rotation);

  return [
    rx * c * ax, rx * (s / a) * ay, rx * c * bx + rx * (s / a) * by + ccx,
    -ry * s * a * ax, ry * c * ay, -ry * s * a * bx + ry * c * by + ccy,
    0, 0, 1,
  ];
}

/**
 * The inverse of {@link computeBackgroundUvMatrix} for one point: which screen
 * uv (0..1, origin bottom-left) shows texture uv `(u, v)`. Null only for a
 * degenerate framing.
 */
export function screenUvForTextureUv(
  framing: Required<BackgroundImageFraming>,
  viewAspect: number,
  imageAspect: number,
  u: number,
  v: number,
): [number, number] | null {
  const m = computeBackgroundUvMatrix(framing, viewAspect, imageAspect);
  const a = m[0], b = m[1], c = m[3], d = m[4];
  const det = a * d - b * c;
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null;
  const x = u - m[2];
  const y = v - m[5];
  return [(d * x - b * y) / det, (-c * x + a * y) / det];
}

// ─── The host's half ────────────────────────────────────────────────────────

/** Which texture uv screen uv `(su, sv)` samples — the forward matrix itself,
 *  i.e. exactly what the backdrop draws there. */
export function textureUvForScreenUv(
  framing: Required<BackgroundImageFraming>,
  viewAspect: number,
  imageAspect: number,
  su: number,
  sv: number,
): [number, number] {
  const m = computeBackgroundUvMatrix(framing, viewAspect, imageAspect);
  return [m[0] * su + m[1] * sv + m[2], m[3] * su + m[4] * sv + m[5]];
}

/** A point of the canvas (fractions, y down) → the photo point drawn there
 *  (normalised, y down). May fall outside 0..1 where the backdrop does not
 *  cover the canvas (a `contain` fit's smeared border). */
export function screenToPhoto(
  framing: Required<BackgroundImageFraming>,
  viewAspect: number,
  imageAspect: number,
  flipY: boolean,
  sx: number,
  sy: number,
): [number, number] {
  const [u, v] = textureUvForScreenUv(framing, viewAspect, imageAspect, sx, 1 - sy);
  return [u, flipY ? 1 - v : v];
}

/** A photo point (normalised, y down) → where it is drawn on the canvas
 *  (fractions, y down). The viewer's `photoToNdc`, in DOM terms. */
export function photoToScreen(
  framing: Required<BackgroundImageFraming>,
  viewAspect: number,
  imageAspect: number,
  flipY: boolean,
  x: number,
  y: number,
): [number, number] | null {
  const s = screenUvForTextureUv(framing, viewAspect, imageAspect, x, flipY ? 1 - y : y);
  return s ? [s[0], 1 - s[1]] : null;
}
